/**
 * Refusing two categories with the same name (PO decision, 2026-08-19).
 *
 * Scope of the rule: same household, same kind (chi/thu), same name — regardless of parent.
 * Not per-parent, because Quick Add draws every category of a kind in ONE flat grid: two rows
 * reading "Cà phê" there are indistinguishable even if one is filed under "Ăn uống".
 *
 * Hidden categories count as taken. Creating a second "Cà phê" while the first is only hidden
 * is how a household ends up with two of them the moment the old one comes back.
 *
 * Comparison is trimmed, whitespace-collapsed and case-insensitive, but diacritics are kept:
 * "Cà phê" and "Ca phe" are different words, and silently treating them as one would block a
 * name the user deliberately typed.
 *
 * This is a UI guard, not a database constraint. The database has no unique index on category
 * names — a constraint would need a migration and a decision about parents, and the ledger is
 * unaffected either way.
 */
export type NamedCategory = Readonly<{
  id: string;
  householdId: string;
  name: string;
  categoryType: 'expense' | 'income';
  isActive: boolean;
}>;

/** The form of a name used for comparison only — never for storage. */
export function normalizeCategoryName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toLocaleLowerCase('vi');
}

export type DuplicateCheckInput = Readonly<{
  name: string;
  categoryType: 'expense' | 'income';
  householdId: string;
  /** The category being renamed — it must not count as a duplicate of itself. */
  excludeId?: string;
}>;

/** The category already using this name, or null when the name is free. */
export function findDuplicateCategory<T extends NamedCategory>(
  categories: readonly T[],
  input: DuplicateCheckInput,
): T | null {
  const wanted = normalizeCategoryName(input.name);
  if (wanted === '') return null;

  return (
    categories.find(
      (category) =>
        // household_id is checked explicitly even though RLS already scopes every read to the
        // caller's household: the rule is "unique within a household", and it should say so.
        category.householdId === input.householdId &&
        category.categoryType === input.categoryType &&
        category.id !== input.excludeId &&
        normalizeCategoryName(category.name) === wanted,
    ) ?? null
  );
}

/** What to tell the user, including the case where the clash is with a hidden category. */
export function duplicateCategoryMessage(duplicate: NamedCategory): string {
  return duplicate.isActive
    ? `Đã có danh mục tên “${duplicate.name}”. Hãy đặt tên khác.`
    : `Đã có danh mục tên “${duplicate.name}” nhưng đang bị ẩn. Hãy hiện lại danh mục đó thay vì tạo mới.`;
}
